import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, apiClient } from '../App';
import Layout from '../components/Layout';
import { DailyRings } from '../components/DailyRings';
import { TipReactions } from '../components/TipReactions';
import { CoachBioCard } from '../components/CoachBioCard';
import { MonthlySummaryPrompt } from '../components/MonthlySummaryPrompt';
import QuickSetupCard from '../components/QuickSetupCard';
import WeeklyIntentionCard from '../components/WeeklyIntentionCard';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  CheckCircle2, Circle, 
  Trophy, Flame, Zap, ExternalLink, Plus, Minus,
  CalendarDays, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Droplets, Beef, Footprints, Moon, Salad, CandyOff, Dumbbell, 
  CookingPot, ClipboardList, Brain, Wine, Utensils, Target, UtensilsCrossed,
  FileDown, Clock
} from 'lucide-react';
import BadgeDetailsModal from '../components/BadgeDetailsModal';
import BadgeCelebrationModal from '../components/BadgeCelebrationModal';
import { triggerCelebration } from '../components/Celebration';
import TrialBanner from '../components/TrialBanner';
import TrialExpiredModal from '../components/TrialExpiredModal';
import MonthEndCountdownBanner from '../components/MonthEndCountdownBanner';
import { renderTextWithLinks } from '../lib/renderText';

// Icon map for badges (Lucide icons) - expanded
const badgeIconMap = {
  droplets: Droplets,
  beef: Beef,
  footprints: Footprints,
  moon: Moon,
  salad: Salad,
  'candy-off': CandyOff,
  dumbbell: Dumbbell,
  'cooking-pot': CookingPot,
  'clipboard-list': ClipboardList,
  brain: Brain,
  'wine-off': Wine,
  wine: Wine,
  utensils: Utensils,
  trophy: Trophy,
  award: Trophy,
  drumstick: Beef, // Using Beef as fallback since Drumstick may not be imported
  medal: Trophy,
  heart: Trophy,
  zap: Zap,
  flame: Flame,
  star: Trophy,
  ribbon: Trophy,
  calendar: Trophy,
  target: Trophy,
};

// Action emoji icons matching the landing page style
const actionEmojis = {
  'Hit Protein Target': '🥩',
  '2+ Cups Veggies': '🥗',
  'No Refined Sugar': '🚫',
  '2+ Litres Water': '💧',
  'No Alcohol': '🍷',
  'Balanced Dinner': '🍽️',
  'Meal Plan & Prep': '📋',
  'Strength Training': '💪',
  '30 Min Outside': '🚶',
  '7+ Hours Sleep': '😴',
};

// Fallback emojis by icon type
const iconEmojis = {
  'beef': '🥩',
  'salad': '🥗',
  'candy-off': '🚫',
  'droplets': '💧',
  'wine': '🍷',
  'wine-off': '🍷',
  'utensils': '🍽️',
  'clipboard-list': '📋',
  'cooking-pot': '📋',
  'dumbbell': '💪',
  'footprints': '🚶',
  'moon': '😴',
  'brain': '🧠',
};

// Helper to get emoji for an action
const getActionEmoji = (action) => {
  if (actionEmojis[action.name]) {
    return actionEmojis[action.name];
  }
  if (iconEmojis[action.icon]) {
    return iconEmojis[action.icon];
  }
  return '⭐';
};

// Display-name overrides (DB names stay the same as keys elsewhere)
const ACTION_NAME_OVERRIDES = {
  'No Alcohol': 'Limit Alcohol',
  'No Refined Sugar': 'Limit Refined Sugar',
};
const getActionDisplayName = (action) => ACTION_NAME_OVERRIDES[action.name] || action.name;

// Helper function to render text with clickable links
const MyActions = () => {
  const { user, refreshUser } = useAuth();
  const [actions, setActions] = useState([]);
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [challengeInfo, setChallengeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [backdatableDates, setBackdatableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null); // null means "today"
  const [expandedActions, setExpandedActions] = useState({}); // Track which actions have expanded descriptions
  const [dailyTip, setDailyTip] = useState(null); // Daily tip for selected date
  const [tipReactions, setTipReactions] = useState(null);
  const [reacting, setReacting] = useState(false);
  
  // Monthly stats state
  const [, setMonthlyStats] = useState({ points: 0, streak: 0, badges: 0, rank: null });
  
  // Trial status state
  const [trialStatus, setTrialStatus] = useState(null);
  
  // Badge modal state
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [badgeModalOpen, setBadgeModalOpen] = useState(false);
  const [userBadges, setUserBadges] = useState([]); // Enriched badges with milestone info
  
  // Celebration queue state - shows badges one at a time
  const [celebrationQueue, setCelebrationQueue] = useState([]);
  const [currentCelebration, setCurrentCelebration] = useState(null);
  const isProcessingCelebration = useRef(false);

  const toggleActionExpand = (actionId) => {
    setExpandedActions(prev => ({
      ...prev,
      [actionId]: !prev[actionId]
    }));
  };

  // Fetch trial status
  const fetchTrialStatus = async () => {
    try {
      const res = await apiClient.get('/trial-status');
      setTrialStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch trial status:', err);
    }
  };

  // Retry wrapper for API calls
  const fetchWithRetry = async (apiCall, retries = 2) => {
    for (let i = 0; i <= retries; i++) {
      try {
        return await apiCall();
      } catch (err) {
        if (i === retries) throw err;
        await new Promise(r => setTimeout(r, 1000)); // Wait 1s before retry
      }
    }
  };

  // Fetch enriched badges with milestone details
  const fetchUserBadges = async () => {
    try {
      const res = await apiClient.get('/badges');
      setUserBadges(res.data);
    } catch (err) {
      console.error('Failed to fetch badges:', err);
    }
  };

  // Process celebration queue - show one badge at a time
  const processNextCelebration = useCallback(() => {
    if (isProcessingCelebration.current) return;
    
    setCelebrationQueue(prevQueue => {
      if (prevQueue.length === 0) {
        setCurrentCelebration(null);
        return prevQueue;
      }
      
      isProcessingCelebration.current = true;
      const [nextBadge, ...remainingQueue] = prevQueue;
      setCurrentCelebration(nextBadge);
      return remainingQueue;
    });
  }, []);

  // When queue changes, process next item
  useEffect(() => {
    if (!currentCelebration && celebrationQueue.length > 0) {
      processNextCelebration();
    }
  }, [celebrationQueue, currentCelebration, processNextCelebration]);

  // Handle celebration modal close
  const handleCelebrationClose = useCallback(() => {
    isProcessingCelebration.current = false;
    setCurrentCelebration(null);
    // Process next celebration after a short delay
    setTimeout(() => {
      processNextCelebration();
    }, 500);
  }, [processNextCelebration]);

  // Check for recently earned badges and trigger celebration
  const checkForNewBadges = async () => {
    try {
      const res = await apiClient.get('/badges/recent?since_minutes=2');
      if (res.data && res.data.length > 0) {
        // Get already celebrated badges from localStorage
        const celebratedKey = 'celebrated_badges';
        const celebrated = JSON.parse(localStorage.getItem(celebratedKey) || '[]');
        
        // Filter out badges that have already been celebrated
        const newBadges = res.data.filter(badge => {
          // Create a unique key for this badge: milestone_id + earned_at
          const badgeKey = `${badge.milestone_id}_${badge.earned_at}`;
          return !celebrated.includes(badgeKey);
        });
        
        if (newBadges.length > 0) {
          // Mark these badges as celebrated
          const newCelebrated = [
            ...celebrated,
            ...newBadges.map(b => `${b.milestone_id}_${b.earned_at}`)
          ];
          // Keep only last 100 to prevent localStorage bloat
          localStorage.setItem(celebratedKey, JSON.stringify(newCelebrated.slice(-100)));
          
          // Add only new badges to the celebration queue
          setCelebrationQueue(prev => [...prev, ...newBadges]);
        }
        
        // Refresh badges list
        await fetchUserBadges();
        await refreshUser();
      }
    } catch (err) {
      console.error('Failed to check for new badges:', err);
    }
  };

  // Fetch daily tip for the selected date
  const fetchDailyTip = async (date = null) => {
    try {
      const url = date ? `/tips/today?date=${date}` : '/tips/today';
      const res = await apiClient.get(url);
      setDailyTip(res.data);
    } catch (err) {
      console.error('Failed to fetch daily tip:', err);
    }
    try {
      const res = await apiClient.get('/tips/reactions', { params: date ? { date } : {} });
      setTipReactions(res.data);
    } catch (err) {
      setTipReactions(null);
    }
  };

  const handleTipReaction = async (reaction) => {
    const prev = tipReactions;
    setTipReactions({ ...tipReactions, my_reaction: reaction });
    setReacting(true);
    try {
      await apiClient.post('/tips/react', { reaction, date: tipReactions.date });
    } catch (err) {
      setTipReactions(prev);
      toast.error(err.response?.data?.detail || 'Could not save your reaction');
    } finally {
      setReacting(false);
    }
  };

  useEffect(() => {
    // Defer fetcher invocations to next tick so React-compiler's
    // set-state-in-effect rule doesn't flag the async setState writes.
    const tid = setTimeout(() => {
      fetchData();
      fetchBackdatableDates();
      fetchUserBadges();
      fetchDailyTip(); // Fetch today's tip on initial load
      fetchTrialStatus(); // Check if user is on trial
    }, 0);
    return () => clearTimeout(tid);
  }, []);

  useEffect(() => {
    // When selected date changes, fetch the check-in and tip for that date
    const tid = setTimeout(() => {
      if (selectedDate) {
        fetchCheckinForDate(selectedDate);
        fetchDailyTip(selectedDate);
      } else {
        fetchData();
        fetchDailyTip(); // Fetch today's tip
      }
    }, 0);
    return () => clearTimeout(tid);
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      // Fetch critical data first (actions), then others
      const actionsRes = await fetchWithRetry(() => apiClient.get('/actions'));
      setActions(actionsRes.data);
      
      // Fetch remaining data in parallel, but don't fail if some don't load
      const [checkinRes, infoRes, monthlyRes] = await Promise.allSettled([
        fetchWithRetry(() => apiClient.get('/checkins/today')),
        fetchWithRetry(() => apiClient.get('/challenges/info')),
        fetchWithRetry(() => apiClient.get('/leaderboard/me')),
      ]);
      
      if (checkinRes.status === 'fulfilled') {
        setTodayCheckin(checkinRes.value.data);
      }
      if (infoRes.status === 'fulfilled') {
        setChallengeInfo(infoRes.value.data);
      }
      if (monthlyRes.status === 'fulfilled') {
        setMonthlyStats({
          points: monthlyRes.value.data.monthly_points || 0,
          streak: monthlyRes.value.data.current_streak || 0,
          badges: monthlyRes.value.data.badges_count || 0,
          rank: monthlyRes.value.data.rank || null
        });
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Having trouble loading. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackdatableDates = async () => {
    try {
      const res = await fetchWithRetry(() => apiClient.get('/checkins/backdatable-dates'));
      setBackdatableDates(res.data);
    } catch (err) {
      console.error('Failed to fetch backdatable dates:', err);
    }
  };

  const fetchCheckinForDate = async (date) => {
    try {
      const checkinRes = await apiClient.get(`/checkins/today?date=${date}`);
      setTodayCheckin(checkinRes.data);
    } catch (err) {
      console.error('Failed to fetch data for date:', err);
      setTodayCheckin(null);
    }
  };

  const toggleAction = async (actionId) => {
    const checkedIds = todayCheckin?.action_ids || [];
    
    if (checkedIds.includes(actionId)) {
      // Already checked - do nothing (use remove button)
      return;
    }
    
    // Check if adding would exceed 10 points
    const newIds = [...checkedIds, actionId];
    
    setChecking(true);
    try {
      const payload = { action_ids: newIds };
      if (selectedDate) {
        payload.date = selectedDate;
      }
      const res = await apiClient.post('/checkins', payload);
      setTodayCheckin(res.data);
      await refreshUser();
      await fetchBackdatableDates(); // Refresh backdatable dates
      // Refresh monthly stats
      try {
        const monthlyRes = await apiClient.get('/leaderboard/me');
        setMonthlyStats({
          points: monthlyRes.data.monthly_points || 0,
          streak: monthlyRes.data.current_streak || 0,
          badges: monthlyRes.data.badges_count || 0,
          rank: monthlyRes.data.rank || null
        });
      } catch (e) { /* ignore */ }
      toast.success('Action completed! +1 point');
      
      // Check for newly earned badges after a short delay (to let backend process)
      setTimeout(() => {
        checkForNewBadges();
      }, 1500);
    } catch (err) {
      // Show the max points message
      const errorMsg = err.response?.data?.detail || 'Failed to update check-in';
      if (errorMsg.includes('10 points')) {
        toast.error(errorMsg, { duration: 4000 });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setChecking(false);
    }
  };

  const removeAction = async (actionId) => {
    const checkedIds = todayCheckin?.action_ids || [];
    if (!checkedIds.includes(actionId)) return;
    
    const newIds = checkedIds.filter(id => id !== actionId);
    
    setChecking(true);
    try {
      const payload = { action_ids: newIds };
      if (selectedDate) {
        payload.date = selectedDate;
      }
      const res = await apiClient.post('/checkins', payload);
      setTodayCheckin(res.data);
      await refreshUser();
      await fetchBackdatableDates(); // Refresh backdatable dates
      // Refresh monthly stats
      try {
        const monthlyRes = await apiClient.get('/leaderboard/me');
        setMonthlyStats({
          points: monthlyRes.data.monthly_points || 0,
          streak: monthlyRes.data.current_streak || 0,
          badges: monthlyRes.data.badges_count || 0,
          rank: monthlyRes.data.rank || null
        });
      } catch (e) { /* ignore */ }
      toast.success('Point removed (-1)');
    } catch (err) {
      toast.error('Failed to remove point');
    } finally {
      setChecking(false);
    }
  };

  const todayPoints = todayCheckin?.total_points || 0;
  const maxPoints = 10;
  const progressPercent = (todayPoints / maxPoints) * 100;
  const checkedIds = todayCheckin?.action_ids || [];

  // Get display date info
  const getSelectedDateDisplay = () => {
    if (!selectedDate) {
      return { label: 'Today', isToday: true };
    }
    const date = new Date(selectedDate + 'T12:00:00');
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const diffDays = Math.floor((today - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return { label: 'Today', isToday: true };
    if (diffDays === 1) return { label: 'Yesterday', isToday: false };
    return { 
      label: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }), 
      isToday: false 
    };
  };

  const dateDisplay = getSelectedDateDisplay();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-3 sm:p-4 md:p-8 pb-24 sm:pb-20 max-w-7xl mx-auto w-full box-border" style={{ maxWidth: '100%', overflowX: 'hidden' }}>
        {/* Header */}
        <div className="mb-4 sm:mb-6 md:mb-8 animate-fade-in">
          <h1 className="font-heading text-xl sm:text-2xl md:text-4xl font-bold text-stone-900 mb-1 md:mb-2 break-words">
            Hey, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-stone-600 text-sm md:text-base break-words">
            {challengeInfo?.is_challenge_active 
              ? `Day ${challengeInfo.current_day} of 28 • ${challengeInfo.days_remaining} days remaining`
              : 'Challenge runs from the 1st to 28th of each month'
            }
          </p>
        </div>

        {/* Month-end countdown banner — only renders on the 29th before 2pm PT */}
        <MonthEndCountdownBanner />
        <QuickSetupCard />

        {/* Date Selector for Backdating - Shown whenever the API returns at
            least one backdatable date. This keeps the picker visible during
            the off-day grace window (29th-31st) so users can still log the 28th. */}
        {/* Check-in dates (left) + daily rings (right) */}
        <div className="mb-3 sm:mb-4 md:mb-6 grid grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-12 gap-2 sm:gap-3 md:gap-4 lg:gap-6 items-stretch animate-fade-in" data-testid="checkin-rings-row">
        {backdatableDates.length > 0 ? (
          <div className="min-w-0 lg:col-span-8">
            <div className="bg-white border border-stone-200 rounded-xl p-3 md:p-4 h-full overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mb-2 sm:mb-3">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                  <CalendarDays className="text-[#EFB342]" size={16} />
                  <span className="text-sm font-medium text-stone-900">
                    {dateDisplay.isToday ? "Today's Check-in" : `Editing: ${dateDisplay.label}`}
                  </span>
                  {!dateDisplay.isToday && (
                    <span className="text-[10px] sm:text-xs bg-orange-500/20 text-orange-400 px-1.5 sm:px-2 py-0.5 rounded">
                      Backdating
                    </span>
                  )}
                </div>
                {selectedDate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedDate(null)}
                    className="text-[#B8871E] hover:text-[#8a6416] hover:bg-[#EFB342]/15 text-xs h-7 px-2 -ml-2 sm:ml-0"
                    data-testid="back-to-today-btn"
                  >
                    Back to Today
                  </Button>
                )}
              </div>
              <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1">
                {backdatableDates.map((dateInfo) => (
                  <button
                    key={dateInfo.date}
                    onClick={() => setSelectedDate(dateInfo.is_today ? null : dateInfo.date)}
                    className={`flex-shrink-0 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 rounded-lg text-center min-w-[52px] sm:min-w-[70px] md:min-w-[85px] transition-all ${
                      (dateInfo.is_today && !selectedDate) || selectedDate === dateInfo.date
                        ? 'bg-[#EFB342] text-black'
                        : dateInfo.has_checkin
                          ? 'bg-[#EFB342]/20 text-[#EFB342] border border-[#EFB342]/30'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                  >
                    <div className="text-[9px] sm:text-[10px] md:text-xs uppercase opacity-70">
                      {dateInfo.is_today ? 'Today' : dateInfo.is_previous_month 
                        ? dateInfo.month 
                        : new Date(dateInfo.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="text-base sm:text-lg md:text-2xl font-bold">{dateInfo.day}</div>
                    {dateInfo.has_checkin && (
                      <div className="text-[9px] sm:text-[10px] md:text-xs">{dateInfo.points} {dateInfo.points === 1 ? 'pt' : 'pts'}</div>
                    )}
                  </button>
                ))}
              </div>
              <p className="text-[11px] sm:text-xs text-stone-500 mt-2">
                Tap a date & then scroll down to add or edit points for the last 7 days
              </p>
            </div>
          </div>
        ) : <div className="lg:col-span-8" />}
          <div className="lg:col-span-4">
          <DailyRings
            actionsDone={checkedIds.length}
            actionsTotal={actions.length}
            coachingDone={!!tipReactions?.my_reaction}
          />
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-3 sm:gap-4 md:gap-6">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-8 space-y-3 sm:space-y-4 md:space-y-6">
            {/* Trial Banner - Only shown for trial users */}
            {trialStatus?.is_trial && !trialStatus?.is_expired && (
              <TrialBanner 
                daysRemaining={trialStatus.days_remaining} 
                salesPageUrl={trialStatus.sales_page_url}
              />
            )}
            
            {/* Daily GRIT Tip - Shows tip for selected date */}
            {dailyTip && (
              <div className="animate-fade-in">
                <div className="bg-white border border-stone-200 rounded-xl md:rounded-2xl overflow-hidden" data-testid="daily-tip-card">
                  <div className="p-4 sm:p-5 md:p-6">
                    <div className="flex items-center gap-2 mb-2 sm:mb-3">
                      <Zap className="text-[#EFB342]" size={16} />
                      <span className="text-[10px] sm:text-xs text-stone-500 uppercase tracking-wider">
                        {selectedDate ? `${dateDisplay.label}'s GRIT Intel` : "Today's GRIT Intel"}
                      </span>
                    </div>
                    <h3 className="font-heading text-base sm:text-lg md:text-xl font-bold text-stone-900 mb-2">{dailyTip.title}</h3>
                    <p className="text-stone-700 text-sm leading-relaxed whitespace-pre-wrap">{renderTextWithLinks(dailyTip.content)}</p>
                    
                    {dailyTip.external_link && (
                      <a 
                        href={dailyTip.external_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#EFB342] text-xs sm:text-sm font-medium hover:underline mt-3"
                      >
                        Learn more <ExternalLink size={14} />
                      </a>
                    )}
                    
                    {dailyTip.pdf_url && (
                      <a 
                        href={dailyTip.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#EFB342]/10 border border-[#EFB342]/30 text-[#EFB342] text-xs sm:text-sm font-medium px-3 py-2 rounded-lg hover:bg-[#EFB342]/20 transition-colors mt-3"
                      >
                        <FileDown size={16} />
                        Download Recipe/Guide (PDF)
                      </a>
                    )}

                    <TipReactions
                      options={tipReactions?.options}
                      myReaction={tipReactions?.my_reaction}
                      onReact={handleTipReaction}
                      busy={reacting}
                    />
                  </div>
                  {/* Priority: image_url > gif_url
                      object-contain ensures the full image is always visible
                      (no crop), letterboxed against the card background if the
                      aspect ratio is taller/wider than the frame. */}
                  {dailyTip.image_url && (
                    <div className="w-full bg-stone-50 flex items-center justify-center">
                      <img 
                        src={dailyTip.image_url} 
                        alt={dailyTip.title}
                        className="w-full max-h-[450px] md:max-h-[550px] object-contain"
                      />
                    </div>
                  )}
                  {dailyTip.gif_url && !dailyTip.image_url && (
                    <div className="w-full bg-stone-50 flex items-center justify-center">
                      <img 
                        src={dailyTip.gif_url} 
                        alt={dailyTip.title}
                        className="w-full max-h-[450px] md:max-h-[550px] object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Daily Progress Card */}
            <div className="bg-white border border-stone-200 rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 animate-fade-in stagger-1" data-testid="daily-progress-card">
              <div className="flex items-center justify-between mb-3 sm:mb-4 md:mb-6">
                <div className="flex-1 min-w-0 mr-3">
                  <h2 className="font-heading text-base sm:text-lg md:text-xl font-bold text-stone-900">
                    {dateDisplay.isToday ? 'KEY METABOLIC STRATEGIES' : `${dateDisplay.label.toUpperCase()}'S KEY METABOLIC STRATEGIES`}
                  </h2>
                  <p className="text-stone-500 text-[11px] sm:text-xs md:text-sm mt-0.5 sm:mt-1">
                    {dateDisplay.isToday ? 'Take action today & collect points!' : 'Edit the strategies you crushed this day'}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-[#EFB342]">{todayPoints}</p>
                  <p className="text-stone-500 text-[9px] sm:text-[10px] md:text-xs uppercase">of {maxPoints} pts</p>
                </div>
              </div>
              
              <Progress value={progressPercent} className="h-2 md:h-3 bg-stone-100 mb-3 sm:mb-4 md:mb-6" />
              
              {/* Actions Grid - 2 columns on larger screens */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3 md:gap-4">
                {actions.map((action) => {
                  const isChecked = checkedIds.includes(action.id);
                  const emoji = getActionEmoji(action);
                  const isExpanded = expandedActions[action.id];
                  
                  return (
                    <div
                      key={action.id}
                      className={`p-3 sm:p-4 rounded-xl border transition-all duration-300 ${
                        isChecked 
                          ? 'bg-[#EFB342]/10 border-[#EFB342]/60 shadow-sm' 
                          : 'bg-white border-stone-200 hover:border-[#EFB342]/50 hover:shadow-sm'
                      }`}
                      data-testid={`action-${action.id}`}
                    >
                      <div className="flex items-start gap-3 sm:gap-4">
                        <button
                          onClick={() => !isChecked && toggleAction(action.id)}
                          disabled={checking || isChecked}
                          className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                            isChecked ? 'bg-[#EFB342] cursor-default' : 'bg-[#F0EBE1] hover:bg-[#EFB342]/20 cursor-pointer'
                          }`}
                        >
                          {isChecked ? (
                            <CheckCircle2 size={24} className="sm:w-7 sm:h-7 text-black" />
                          ) : (
                            <span className="text-2xl sm:text-3xl">{emoji}</span>
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm sm:text-base text-stone-900 leading-tight">
                            {getActionDisplayName(action)}
                          </p>
                          <div 
                            className="mt-1 cursor-pointer"
                            onClick={() => toggleActionExpand(action.id)}
                          >
                            {isExpanded ? (
                              <p className="text-xs sm:text-sm text-stone-700 leading-relaxed">
                                {renderTextWithLinks(action.description)}
                              </p>
                            ) : (
                              <p className="text-xs sm:text-sm text-stone-700 leading-relaxed">
                                {(() => {
                                  const plain = (action.description || '').replace(/\*\*/g, '');
                                  return plain.length > 100 ? plain.substring(0, 100) + '...' : plain;
                                })()}
                              </p>
                            )}
                            {action.description && action.description.replace(/\*\*/g, '').length > 100 && (
                              <button className="flex items-center gap-1 text-xs text-[#B8871E] font-semibold mt-1 hover:underline">
                                {isExpanded ? (
                                  <>Show less <ChevronUp size={12} /></>
                                ) : (
                                  <>Show more <ChevronDown size={12} /></>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end mt-3">
                        {isChecked ? (
                          <button
                            onClick={() => removeAction(action.id)}
                            disabled={checking}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-700 text-xs font-semibold border border-red-200 transition-colors"
                            data-testid={`remove-action-${action.id}`}
                          >
                            <Minus size={13} />
                            <span>Remove</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => toggleAction(action.id)}
                            disabled={checking}
                            className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#EFB342] hover:bg-[#D99F30] text-black text-xs font-bold uppercase tracking-wider shadow-sm transition-colors"
                            data-testid={`add-action-${action.id}`}
                          >
                            <Plus size={13} strokeWidth={3} />
                            <span>Add</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Cheat Sheet Download Link */}
            <div className="mt-4 text-center">
              <a
                href="https://buildnutrition.ca/wp-content/uploads/2026/10/RxGRIT-Daily-Action-Blueprint.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#B8871E] hover:text-[#EFB342] text-sm font-semibold underline underline-offset-4 decoration-[#EFB342]/40 hover:decoration-[#EFB342] transition-colors"
                data-testid="myactions-cheatsheet-link"
              >
                <FileDown size={16} />
                Download your 10 Daily Actions Cheat Sheet for quick reference.
              </a>
            </div>
          </div>

          {/* Sidebar - Right Side */}
          <div className="lg:col-span-4 space-y-4 md:space-y-6">
            {/* Badges Preview - Current Month Only, Most Recent First */}
            {(() => {
              // Filter badges to current month and sort by most recent
              const now = new Date();
              const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
              
              const badgesToShow = (userBadges.length > 0 ? userBadges : (user?.badges || []))
                .filter(badge => {
                  const earnedAt = badge.earned_at || '';
                  return earnedAt >= currentMonthStart;
                })
                .sort((a, b) => {
                  const dateA = a.earned_at || '';
                  const dateB = b.earned_at || '';
                  return dateB.localeCompare(dateA); // Most recent first
                });
              
              if (badgesToShow.length === 0) return null;
              
              return (
                <div className="bg-white border border-stone-200 rounded-2xl p-6 animate-fade-in stagger-3">
                  <h3 className="font-heading text-lg font-bold text-stone-900 mb-1">THIS MONTH&apos;S BADGES</h3>
                  <p className="text-stone-500 text-xs mb-3">Tap a badge to see details</p>
                  <div className="flex flex-wrap gap-3">
                    {badgesToShow.map((badge, i) => {
                      const BadgeIcon = badgeIconMap[badge.badge_icon] || Trophy;
                      return (
                        <button 
                          key={i}
                          onClick={() => {
                            setSelectedBadge(badge);
                            setBadgeModalOpen(true);
                          }}
                          className="w-14 h-14 bg-gradient-to-br from-[#EFB342] to-[#D99F30] hexagon flex items-center justify-center hover:scale-110 transition-transform cursor-pointer shadow-lg shadow-[#EFB342]/20"
                          title={badge.badge_name}
                          data-testid={`badge-${i}`}
                        >
                          <BadgeIcon size={24} className="text-black" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Resource Links - Some locked for trial users, Recipe Database and Free Tracking App available to all */}
            <div className="space-y-2 animate-fade-in stagger-3">
              {/* Featured: FREE Beyond Estrogen Guide — paid members only */}
              {!trialStatus?.is_trial && (
                <a
                  href="https://buildnutrition.ca/wp-content/uploads/2026/09/beyond-estrogen-guide-2.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block relative overflow-hidden p-3 sm:p-4 rounded-xl ring-2 ring-[#EFB342] hover:ring-[#B8871E] shadow-md hover:shadow-lg transition-all group bg-gradient-to-br from-[#FFF8E7] to-[#F9E9B8]"
                  data-testid="sidebar-beyond-estrogen"
                >
                  <span className="inline-block text-[9px] font-bold tracking-[0.18em] uppercase text-[#B8871E] mb-1">
                    Bonus Guide
                  </span>
                  <h4 className="font-heading font-bold text-stone-900 text-sm leading-tight">
                    FREE Guide: Beyond Estrogen
                  </h4>
                  <p className="text-stone-700 text-xs mt-1 break-words leading-snug italic">
                    A Guide to the Hidden Hormonal Players Dictating Your Weight, Energy &amp; Sleep.
                  </p>
                </a>
              )}

              {trialStatus?.is_trial ? (
                // Trial users: Toolbox, 1:1 Support, and Recipe Database locked
                <>
                  <div className="block p-2 sm:p-3 bg-stone-50 border-2 border-stone-300 rounded-xl opacity-70 cursor-not-allowed">
                    <h4 className="font-semibold text-stone-600 text-sm">
                      Access the Toolbox
                    </h4>
                    <p className="text-stone-500 text-xs mt-1 break-words">
                      Meal plans, recipes, macro guides, templates and more - DIY resources to crush the monthly challenges.
                    </p>
                    <p className="text-xs text-amber-500 mt-1">🔒 Available with full membership</p>
                  </div>
                  <div className="block p-2 sm:p-3 bg-stone-50 border-2 border-stone-300 rounded-xl opacity-70 cursor-not-allowed">
                    <h4 className="font-semibold text-stone-600 text-sm">
                      Book 1:1 Support
                    </h4>
                    <p className="text-stone-500 text-xs mt-1 break-words">
                      DIY most days, 50% OFF coaching when needed
                    </p>
                    <p className="text-xs text-amber-500 mt-1">🔒 Discount available with full membership</p>
                  </div>
                  <div className="block p-2 sm:p-3 bg-stone-50 border-2 border-stone-300 rounded-xl opacity-70 cursor-not-allowed">
                    <h4 className="font-semibold text-stone-600 text-sm">
                      Recipe Database
                    </h4>
                    <p className="text-stone-500 text-xs mt-1 break-words">
                      <span className="font-bold text-stone-600">*NEW*</span> Search through hundreds of healthy recipes - find exactly what you need at your fingertips!
                    </p>
                    <p className="text-xs text-amber-500 mt-1">🔒 Available with full membership</p>
                  </div>
                  <a 
                    href="https://buildnutrition.ca/pb-free-tracking-app" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block p-2 sm:p-3 bg-[#EFB342]/15 border-2 border-[#EFB342]/40 rounded-xl hover:bg-[#EFB342]/25 hover:border-[#EFB342]/60 transition-all group"
                  >
                    <h4 className="font-semibold text-[#EFB342] text-sm">
                      Free Macro Tracking App
                    </h4>
                    <p className="text-stone-700 text-xs mt-1 break-words">
                      Optional — but a powerful tool if you want extra clarity and precision.
                    </p>
                  </a>
                </>
              ) : (
                // Clickable versions for paid users
                <>
                  <a 
                    href="https://buildnutrition.ca/rxgrit-toolbox" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block p-2 sm:p-3 bg-[#EFB342]/15 border-2 border-[#EFB342]/40 rounded-xl hover:bg-[#EFB342]/25 hover:border-[#EFB342]/60 transition-all group"
                  >
                    <h4 className="font-semibold text-[#EFB342] text-sm">
                      Access the Toolbox
                    </h4>
                    <p className="text-stone-700 text-xs mt-1 break-words">
                      Meal plans, recipes, macro guides, templates and more - DIY resources to crush the monthly challenges.
                    </p>
                  </a>
                  <a 
                    href="https://p.bttr.to/3LHEaO9" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block p-2 sm:p-3 bg-[#EFB342]/15 border-2 border-[#EFB342]/40 rounded-xl hover:bg-[#EFB342]/25 hover:border-[#EFB342]/60 transition-all group"
                  >
                    <h4 className="font-semibold text-[#EFB342] text-sm">
                      Book 1:1 Support
                    </h4>
                    <p className="text-stone-700 text-xs mt-1 break-words">
                      DIY most days, 50% OFF coaching when needed [use discount code <span className="font-bold text-red-500">RxGRIT50</span>]
                    </p>
                  </a>
                  <a 
                    href="https://app.thatcleanlife.com/shares/dd080ea7-162e-4480-8c0a-68a0fab08c56" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block p-2 sm:p-3 bg-[#EFB342]/15 border-2 border-[#EFB342]/40 rounded-xl hover:bg-[#EFB342]/25 hover:border-[#EFB342]/60 transition-all group"
                  >
                    <h4 className="font-semibold text-[#EFB342] text-sm">
                      Recipe Database
                    </h4>
                    <p className="text-stone-700 text-xs mt-1 break-words">
                      <span className="font-bold text-[#EFB342]">*NEW*</span> Use password <span className="font-bold text-red-500">RxGRIT</span> to access, type your keyword into the &apos;Filter&apos; field & voila, find what you need at your fingertips!
                    </p>
                  </a>
                  <a 
                    href="https://buildnutrition.ca/pb-free-tracking-app" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block p-2 sm:p-3 bg-[#EFB342]/15 border-2 border-[#EFB342]/40 rounded-xl hover:bg-[#EFB342]/25 hover:border-[#EFB342]/60 transition-all group"
                  >
                    <h4 className="font-semibold text-[#EFB342] text-sm">
                      Free Macro Tracking App
                    </h4>
                    <p className="text-stone-700 text-xs mt-1 break-words">
                      Optional — but a powerful tool if you want extra clarity and precision.
                    </p>
                  </a>
                </>
              )}
            </div>

            <CoachBioCard />
          </div>
        </div>
      </div>

      <WeeklyIntentionCard />
      <MonthlySummaryPrompt />
      
      {/* Badge Details Modal */}
      <BadgeDetailsModal 
        badge={selectedBadge}
        isOpen={badgeModalOpen}
        onClose={() => {
          setBadgeModalOpen(false);
          setSelectedBadge(null);
        }}
      />
      
      {/* Badge Celebration Modal - Shows earned badges one at a time */}
      <BadgeCelebrationModal
        isOpen={!!currentCelebration}
        onClose={handleCelebrationClose}
        badge={currentCelebration}
      />
      
      {/* Trial Expired Modal - Shows when trial has ended */}
      {trialStatus?.is_trial && trialStatus?.is_expired && (
        <TrialExpiredModal 
          pointsEarned={user?.total_points || 0}
          salesPageUrl={trialStatus.sales_page_url}
        />
      )}
    </Layout>
  );
};

export default MyActions;
